import { Injectable } from '@angular/core';
import { Observable } from "rxjs";
import {
    IAirGetQuotedAvailabilityRequest,
    IAirQuotedAvailabilityTraveller
} from "libraries/service/resources/model/booking/air/IAirQuotedAvailability";
import {
    IAirQuotedAvailabilityResponse
} from "libraries/service/resources/model/booking/air/IAirQuotedAvailabilityResponse";
import { map, mergeMap } from "rxjs/operators";
import { ISamAgent } from "libraries/service/resources/sam/ISamAgent";
import { ISamCompany } from "libraries/service/resources/sam/ISamCompany";
import { PropalService } from "app_locobook/service/propal/propal.service";
import { UserAuthService } from "libraries/service/user-auth/user-auth.service";
import { CacheSamResourceService } from "libraries/service/resources/sam/cache-sam-resource.service";
import { QuotedAvailabilityService } from "app_locobook/service/quoted-availability.service";
import { ResponseForConvertOfferAfx2jsonType } from "@afidium/ts-facade";
import {
    CacheQuotedAvailabilityService
} from "app_locobook/service/cache-quoted-availability/cache-quoted-availability.service";
import { IFareFamily } from "libraries/service/resources/booking/interfaces/IGetBookings";
import {
    IAirCreateBookingRequestSegment,
    IAirCreateBookingRequestTraveller
} from "libraries/service/resources/model/booking/air/IAirCreateBooking";
import _cloneDeep from "lodash-es/cloneDeep";
import { AirTravellerType } from "libraries/service/resources/model/booking/air/IAirTraveller";


interface IRequestInformations {
    userId: number;
    selectedJourneys: string[];
    companyId: number;
    locator: string;
}

@Injectable()
export class AncillaryServicesService {

    constructor(
        private readonly propalService: PropalService,
        private readonly userAuthService: UserAuthService,
        private readonly cacheSamResourceService: CacheSamResourceService,
        private readonly cacheQuotedAvailabilityService: CacheQuotedAvailabilityService,
        private readonly quotedAvailabilityService: QuotedAvailabilityService,
    ) {
    }

    public getQuote(
        bookingKey: string, alternativeOfferXId: string
    ): Observable<{ request: IAirGetQuotedAvailabilityRequest, response: IAirQuotedAvailabilityResponse }> {

        const requestInformations: IRequestInformations = this.getRequestInformations();

        return this.cacheSamResourceService
            .getAgentAndCompany(requestInformations.userId, requestInformations.companyId)
            .pipe(
                map(([samAgent, samCompany]: [ISamAgent, ISamCompany]): IAirGetQuotedAvailabilityRequest => {
                    return this.quotedAvailabilityService.buildRequest(
                        bookingKey,
                        requestInformations.selectedJourneys,
                        samAgent,
                        samCompany,
                        requestInformations.locator,
                    );
                }),
                mergeMap((request: IAirGetQuotedAvailabilityRequest)
                    : Observable<{ request: IAirGetQuotedAvailabilityRequest, response: IAirQuotedAvailabilityResponse }> => {
                    return this.quotedAvailabilityService.sendRequest(
                        request,
                        bookingKey,
                        alternativeOfferXId,
                    );
                }),
            );
    }

    public getQuoteWithService(
        bookingKey: string,
        fareFamily?: { [key: string]:IFareFamily },
        segment?: IAirCreateBookingRequestSegment,
        travellers?: { [key: string]: IAirCreateBookingRequestTraveller },
        storeRequestBeforeCreateBooking?: boolean
    ): Observable<ResponseForConvertOfferAfx2jsonType> {

        if(travellers && Object.keys(travellers).length){
            const originalRequest:  IAirGetQuotedAvailabilityRequest = _cloneDeep(this.cacheQuotedAvailabilityService.getRequest(bookingKey));

            const updatedTravellers: IAirQuotedAvailabilityTraveller[] = originalRequest.traveller
                .map((originalTraveller: IAirQuotedAvailabilityTraveller): IAirQuotedAvailabilityTraveller => {
                    return {
                        ...originalTraveller,
                        title: travellers[originalTraveller.id].title,
                        firstName: travellers[originalTraveller.id].firstName,
                        lastName: travellers[originalTraveller.id].lastName,
                    };
                });
            this.cacheQuotedAvailabilityService.updateRequest(bookingKey, {...originalRequest, traveller: updatedTravellers});
        }

        return this.quotedAvailabilityService.sendRequestWithService(
            {
                ...this.cacheQuotedAvailabilityService.getRequest(bookingKey),
                // @ts-ignore
                fareFamily,
                segment,
            },
            bookingKey,
            storeRequestBeforeCreateBooking,
        );
    }

    private getRequestInformations(): IRequestInformations {
        return {
            userId: this.userAuthService.getUserId(),
            selectedJourneys: this.propalService.propalResponse.display.selectedJourneys,
            companyId: this.userAuthService.getCompanyId(),
            locator: this.propalService.propalResponse.searchRQ?.body?.locator,
        };
    }
}
